import React from "react"
import * as Yup from "yup"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import { Box, FormLabel, Text } from "@chakra-ui/react"
import SelectMenu from "components/ReactHookForm/SelectMenu"
import { LanguageTemplate } from "sharedTypes"

/** Sentinel "Start From" value that creates a template with every field left blank. */
export const BLANK_TEMPLATE_ID = "blank"

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Please enter a name"),
  baseTemplateId: Yup.string(),
})

// Same aligned label-column grid as EditProfile.tsx — a fixed label column keeps every row's
// label and input aligned, which a per-field label (each sized to its own content) can't do.
const LABEL_COL = "12rem"
const GRID_SX = { "& > *": { marginBottom: "0 !important" } }

const Label = ({ children }: { children: string }) => (
  <FormLabel fontSize="md" mb={0}>{children}</FormLabel>
)

type Props = {
  mode: "create" | "rename"
  initialName?: string
  templates?: LanguageTemplate[]
  /** Pre-selects "Template to Duplicate" — used when Duplicate is triggered from a specific row. */
  initialBaseTemplateId?: string
  onClose: () => void
  onSubmit: (values: { name: string, baseTemplateId?: string }) => Promise<void>
}

const TemplateNameModal = ({
  mode, initialName = "", templates = [], initialBaseTemplateId, onClose, onSubmit,
}: Props) => {
  const isCreate = mode === "create"

  return (
    <ModalForm
      title={isCreate ? "Duplicate Language Template" : "Rename Language Template"}
      size="xl"
      initialValues={{
        name: initialName,
        baseTemplateId: initialBaseTemplateId ?? templates[0]?.id,
      }}
      validationSchema={validationSchema}
      submitText={isCreate ? "Duplicate" : "Rename"}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      {({ control: formControl }) => (
        <Box
          display="grid"
          gridTemplateColumns={`${LABEL_COL} 1fr`}
          rowGap={4}
          columnGap={4}
          alignItems="center"
          sx={GRID_SX}
        >
          <Label>{isCreate ? "New Template Name" : "Template Name"}</Label>
          <TextField
            name="name"
            control={formControl}
            placeholder={isCreate ? "Enter a template name" : "e.g. French, or Playful RSVP"}
            autoComplete="off"
            variant="outline"
          />
          {isCreate && (
            <>
              <Label>Template to Duplicate</Label>
              <SelectMenu
                name="baseTemplateId"
                control={formControl}
                options={[
                  ...templates.map((t) => ({ label: t.name, value: t.id })),
                  { label: "Scratch (blank)", value: BLANK_TEMPLATE_ID },
                ]}
              />
              <Text gridColumn="1 / -1">
                The duplicate will be an independent copy, so changes
                won&apos;t affect the original.
              </Text>
            </>
          )}
        </Box>
      )}
    </ModalForm>
  )
}

export default TemplateNameModal
